import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiService from '../services/api';
import type { Captain } from '../types';

/**
 * 사장님(선주/선장) 계정 목록.
 * 검색 + 페이지네이션 + 비밀번호 리셋 + 삭제.
 * 새 계정 생성은 /captains/new 로 이동.
 */
export default function CaptainList() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Captain[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{
    id: number;
    name: string;
    newPassword: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getCaptains({
        page: page + 1,
        pageSize,
        search: search.trim() || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResetPassword = async (id: number, name: string) => {
    if (!window.confirm(`${name} 사장님의 비밀번호를 리셋하시겠습니까?\n\n새 비밀번호가 생성되며, 사장님께 전달해야 합니다.`)) {
      return;
    }
    try {
      const res = await apiService.resetCaptainPassword(id);
      setResetPasswordDialog({ id, name, newPassword: res.newPassword });
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '비밀번호 리셋 실패');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`${name} 사장님 계정을 삭제하시겠습니까?\n\n담당 배들의 owner 는 해제되며 배 자체는 유지됩니다.`)) {
      return;
    }
    try {
      await apiService.deleteCaptain(id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '삭제 실패');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사됨');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonSearchIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            사장님 계정 관리
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/captains/new')}
        >
          새 사장님 계정 생성
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="아이디 또는 이름 검색"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          fullWidth
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>아이디</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>이메일</TableCell>
              <TableCell>담당 배</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>가입일</TableCell>
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    등록된 사장님 계정이 없습니다
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {c.username}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {c.email ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {c.ownedBoats.length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                          없음
                        </Typography>
                      ) : (
                        c.ownedBoats.map((b) => (
                          <Chip
                            key={b.id}
                            size="small"
                            label={b.name}
                            color={b.isActive ? 'primary' : 'default'}
                          />
                        ))
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {c.isBlocked ? (
                      <Chip size="small" label="정지" color="error" />
                    ) : (
                      <Chip size="small" label="활성" color="success" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(c.createdAt).toLocaleDateString('ko-KR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="비밀번호 리셋">
                      <IconButton
                        size="small"
                        onClick={() => handleResetPassword(c.id, c.name)}
                      >
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(c.id, c.name)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="페이지당"
        />
      </TableContainer>

      {resetPasswordDialog && (
        <Paper
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 4,
            zIndex: 1400,
            minWidth: 400,
            boxShadow: 8,
          }}
        >
          <Typography variant="h6" gutterBottom>
            🔑 비밀번호 리셋 완료
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>{resetPasswordDialog.name}</strong> 사장님의 새 비밀번호입니다.
            이 화면을 닫으면 다시 볼 수 없으니 지금 복사해서 사장님께 전달해주세요.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              my: 2,
            }}
          >
            <Typography
              variant="h5"
              fontFamily="monospace"
              sx={{ flex: 1, wordBreak: 'break-all' }}
            >
              {resetPasswordDialog.newPassword}
            </Typography>
            <IconButton
              onClick={() => handleCopy(resetPasswordDialog.newPassword)}
              color="primary"
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => setResetPasswordDialog(null)}
            >
              확인
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
